import { log } from "@connect/observability";
import type { ChallengeRequired } from "@moonpay/platform-protocol/models/challenge";
import type { BuyCheckBody, BuyCheckResolution } from "../../../../lib/buy/buy-check.types";
import { createStep } from "../../../../lib/buy/create-step";
import { decodeSignedQuote } from "../../../../lib/buy/quote";
import type { SignedQuote } from "../../../../lib/buy/quote.types";
import { mapFailedTransactionResponse } from "../../../../lib/buy/map-transaction-failure";
import { preCheck } from "../../../../lib/buy/pre-check";
import type { PreCheckResult } from "../../../../lib/buy/pre-check.types";
import { runRecheckLoop } from "../../../../lib/buy/recheck-loop";
import { decodeBuyCheckToken } from "./buy-check-token";
import type { BuyCheckTokenParams } from "./buy-check-token.types";
import { challengeTokenOf, configFromToken } from "./challenge-config";
import type { BuyErrorCode, BuyFlow, BuyFlowDeps, BuyFlowState } from "./buy-flow.types";

const WALLET_METHODS = ["apple_pay", "google_pay"];

export function createBuyFlow(deps: BuyFlowDeps, onState: (state: BuyFlowState) => void): BuyFlow {
  let token: string | undefined;
  let quote: SignedQuote | null = null;
  let externalTransactionId: string | undefined;
  let runId = 0;
  let inFlight = false;
  let current: BuyFlowState = { kind: "idle" };

  const set = (state: BuyFlowState) => {
    current = state;
    onState(state);
  };
  const fail = (code: BuyErrorCode, message: string) => set({ kind: "error", code, message });

  /** Starts a run, or returns null if one is already going. */
  const begin = (): number | null => {
    if (inFlight) {
      return null;
    }
    inFlight = true;
    runId += 1;
    return runId;
  };
  const end = (id: number) => {
    if (id === runId) {
      inFlight = false;
    }
  };

  function showChallenge(challenge: ChallengeRequired) {
    // No fallback to the previous token: its nextChallenge is the one already solved.
    const challengeToken = challengeTokenOf(challenge.url);
    if (!challengeToken || !decodeBuyCheckToken(challengeToken)) {
      fail("invalid_challenge", "Could not decode challenge from response");
      return;
    }
    const config = configFromToken(challengeToken);
    if (!config || !deps.hasScreen(config.type)) {
      fail("generic", "This challenge cannot be completed here");
      return;
    }
    token = challengeToken;
    set({ kind: "challenge", config, challengeToken });
  }

  const stateParams = (): BuyCheckTokenParams | null =>
    token ? (decodeBuyCheckToken(token)?.params ?? null) : null;

  async function postCreate(
    transactionId: string,
    id: number,
    resolution?: BuyCheckResolution,
  ): Promise<void> {
    set({ kind: "checking" });
    let pending = resolution;
    await runRecheckLoop({
      transactionId,
      challengeToken: token,
      check: (challengeToken) => {
        const body = {
          transactionId,
          challengeToken,
          ...(pending && { resolution: pending }),
        };
        pending = undefined;
        return deps.check(body);
      },
      emit: {
        complete: async (transaction) =>
          set({
            kind: "complete",
            transaction:
              transaction.status === "failed"
                ? { id: transaction.id, status: "failed", ...mapFailedTransactionResponse() }
                : transaction,
          }),
        challenge: showChallenge,
        error: async (message) => fail("generic", message),
      },
      isCancelled: () => id !== runId,
    });
  }

  /** True once the transaction exists, even if a post-create challenge is still pending. */
  async function create(
    instrument: Parameters<BuyFlowDeps["create"]>[0]["instrument"],
    id: number,
    { detachRecheck = false } = {},
  ): Promise<boolean> {
    if (!quote) {
      fail("missing_quote", "Cannot create transaction without a quote");
      return false;
    }
    set({ kind: "creating" });
    const step = await createStep({
      quote,
      instrument,
      isGuest: deps.isGuest,
      isPartnerIntent: deps.isPartnerIntent,
      create: deps.create,
    });
    if (id !== runId) {
      return false;
    }
    if (step.kind === "quoteExpired") {
      fail("quoteExpired", "Quote expired. Request a new quote.");
      return false;
    }
    if (step.kind === "failed") {
      set({ kind: "complete", transaction: { status: "failed", ...step.failure } });
      return false;
    }
    if (step.kind === "error") {
      fail("generic", step.message);
      return false;
    }
    if (step.kind !== "created") {
      fail("generic", "Could not create the transaction");
      return false;
    }
    const transaction = step.transaction;
    const recheck = deps
      .fingerprint(transaction)
      .then(() => (id === runId ? postCreate(transaction.id, id) : undefined))
      .catch((err) => {
        log.error(err instanceof Error ? err : String(err));
        if (id === runId) {
          fail("generic", "Something went wrong. Please try again.");
        }
      });
    // A wallet's sheet must be completed now, not after the recheck.
    if (!detachRecheck) {
      await recheck;
    }
    return true;
  }

  /** After a pre-create pass: a card or bank transfer is created at once, a wallet waits for its sheet. */
  async function afterPreCheckPass(id: number): Promise<void> {
    if (!quote) {
      fail("missing_quote", "Quote unavailable from challengeToken");
      return;
    }
    if (quote.quote.exchangeRateType === "floating") {
      await create({ kind: "bankTransfer", challengeToken: token }, id);
      return;
    }
    const method = quote.quote.paymentMethod;
    if (method?.type === "card" && method.id) {
      await create({ kind: "card", cardId: method.id, challengeToken: token }, id);
      return;
    }
    if (method && WALLET_METHODS.includes(method.type)) {
      if (!token) {
        fail("configurationError", "challengeToken missing; cannot create transaction");
        return;
      }
      if (deps.handBack) {
        if (deps.handBack(token)) {
          set({ kind: "handedBack" });
          return;
        }
        log.warn("connect.buy.challenge_token_handback_failed");
      }
      set({ kind: "awaitingPaymentSheet", quote, challengeToken: token });
      return;
    }
    fail("unsupportedPaymentMethod", "This payment method cannot be completed here");
  }

  function consumeCheck(result: PreCheckResult, id: number): "pass" | "stop" {
    if (id !== runId) {
      return "stop";
    }
    if (result.outcome === "error") {
      fail("generic", result.message);
      return "stop";
    }
    token = result.challengeToken ?? token;
    if (result.outcome === "challenge") {
      showChallenge(result.challenge);
      return "stop";
    }
    return "pass";
  }

  async function preCreate(
    body: Pick<BuyCheckBody, "quoteSignature" | "challengeToken" | "resolution">,
    id: number,
  ): Promise<void> {
    set({ kind: "checking" });
    const result = await preCheck({
      check: deps.check,
      ...body,
      externalTransactionId,
      isGuest: deps.isGuest,
    });
    if (consumeCheck(result, id) === "pass") {
      await afterPreCheckPass(id);
    }
  }

  async function guarded<T = void>(
    run: (id: number) => Promise<T>,
    fallback?: T,
  ): Promise<T | undefined> {
    const id = begin();
    if (id === null) {
      return fallback;
    }
    try {
      return await run(id);
    } catch (err) {
      log.error(err instanceof Error ? err : String(err));
      if (id === runId) {
        fail("generic", "Something went wrong. Please try again.");
      }
      return fallback;
    } finally {
      end(id);
    }
  }

  return {
    start: (challengeToken) =>
      guarded(async (id) => {
        if (deps.isGuest) {
          fail("unsupported_flow", "Guest checkout is not supported by this challenge");
          return;
        }
        const params = decodeBuyCheckToken(challengeToken)?.params;
        if (!params) {
          fail("invalid_token", "Invalid or unsupported challengeToken");
          return;
        }
        token = challengeToken;
        externalTransactionId = params.externalTransactionId;
        if (params.quoteSignature) {
          quote = decodeSignedQuote(params.quoteSignature);
        }
        if (params.transactionId) {
          await postCreate(params.transactionId, id);
          return;
        }
        if (!quote) {
          fail("missing_quote", "Quote unavailable from challengeToken");
          return;
        }
        await preCreate({ quoteSignature: params.quoteSignature, challengeToken }, id);
      }),

    resolveChallenge: (resolution) =>
      guarded(async (id) => {
        if (current.kind !== "challenge") {
          return;
        }
        const params = stateParams();
        if (!token || !params) {
          fail("configurationError", "challengeToken missing; cannot resolve challenge");
          return;
        }
        if (params.transactionId) {
          await postCreate(params.transactionId, id, resolution);
          return;
        }
        await preCreate(
          { quoteSignature: params.quoteSignature, challengeToken: token, resolution },
          id,
        );
      }),

    resolveWithWallet: async (instrument) =>
      (await guarded(async (id) => {
        if (current.kind !== "awaitingPaymentSheet") {
          return false;
        }
        return create({ ...instrument, challengeToken: token }, id, { detachRecheck: true });
      }, false)) ?? false,

    cancel: () => {
      if (current.kind === "creating") {
        return false;
      }
      runId += 1;
      inFlight = false;
      return true;
    },
  };
}
